import { stripNodeSenders } from "./stripNodeSenders.util";

/** The fields cloning rewrites. Everything else on the campaign is copied verbatim. */
export interface ClonableCampaign {
	id: string;
	name: string;
	isDefault?: boolean;
	isValid?: boolean;
	senders?: unknown;
	/** Carried by campaigns listed from another list; a copy must not keep the source's identity. */
	listId?: unknown;
	listName?: unknown;
	/** The "Stop campaign at" setting; a copy never keeps the source's (see below). */
	stopCampaignAt?: Date | string | null;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	initialNodes?: any[];
}

/** The suffix a cloned campaign's name carries, so the copy is recognisable in the picker. */
export const CLONED_CAMPAIGN_NAME_SUFFIX = "(Copy)";

/** The date while it is still ahead of `now`; `null` when there is none or it has passed. */
const futureStopDateOrNull = (
	stopCampaignAt: Date | string | null | undefined,
	now: Date
): Date | string | null => {
	if (!stopCampaignAt) {
		return null;
	}

	return new Date(stopCampaignAt).getTime() > now.getTime() ? stopCampaignAt : null;
};

/**
 * The campaignConfig to persist when a campaign is duplicated: the existing config plus the copy,
 * keyed by `newCampaignUuid`.
 *
 * The copy deliberately starts **without senders** — neither the campaign's own nor the per-node
 * overrides — and therefore `isValid: false`: the user has to pick who sends before it can run.
 * That is why duplicating is the escape hatch offered by the lock explainer (PREC-3786) rather than
 * a silent one-click fix, and why the explainer says so.
 *
 * The source's end date is never copied (PREC-4322). The copy takes `targetStopDate`, the end date
 * a new campaign on the target list starts with (`eventStopDatePrefill`: the list's event end date
 * while event outreach is on), so a campaign copied from another event, or duplicated after the
 * event moved, ends with this list's event rather than overriding it. Without an event the copy has
 * no end date. A target date that has already passed is dropped too (PREC-4320): the copy would
 * otherwise start expired — it reads RUNNING, its leads are assigned, and the API schedules none.
 *
 * Extracted from `useCampaignActions.cloneCampaign` so the copywriting table and the campaign
 * builder's lock modal duplicate a campaign exactly the same way.
 */
export const buildClonedCampaignConfig = <
	TConfig extends ClonableCampaign,
	TSource extends ClonableCampaign,
>(
	campaignConfig: Record<string, TConfig> | null | undefined,
	source: TSource,
	newCampaignUuid: string,
	targetStopDate: Date | string | null | undefined,
	now: Date = new Date()
): Record<string, TConfig | TSource> => {
	const {
		senders: _senders,
		listId: _listId,
		listName: _listName,
		...sourceWithoutSenders
	} = source;

	return {
		...(campaignConfig ?? {}),
		[newCampaignUuid]: {
			...sourceWithoutSenders,
			name: `${source.name} ${CLONED_CAMPAIGN_NAME_SUFFIX}`,
			id: newCampaignUuid,
			isDefault: false,
			isValid: false,
			senders: [],
			stopCampaignAt: futureStopDateOrNull(targetStopDate, now),
			initialNodes: stripNodeSenders(sourceWithoutSenders.initialNodes),
		} as unknown as TSource,
	};
};
